import React, { useState, useEffect } from "react";
import { api, nairaFormat } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImageUploader, { imgUrl } from "@/components/ImageUploader";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { v: "musician", l: "Musician" },
  { v: "actor", l: "Actor" },
  { v: "photographer", l: "Photographer" },
  { v: "videographer", l: "Videographer" },
  { v: "model", l: "Model" },
  { v: "dancer", l: "Dancer" },
  { v: "comedian", l: "Comedian" },
  { v: "mc", l: "MC / Host" },
  { v: "dj", l: "DJ" },
  { v: "influencer", l: "Influencer" },
  { v: "designer", l: "Designer" },
  { v: "other", l: "Other" },
];

export default function ProfileBuilder({ user, onSaved }) {
  const [category, setCategory] = useState(user.category || "");
  const [location, setLocation] = useState(user.location || "");
  const [bio, setBio] = useState(user.bio || "");
  const [services, setServices] = useState(user.services || []);
  const [avatar, setAvatar] = useState(user.profile_image_path || "");
  const [portfolio, setPortfolio] = useState(user.portfolio_paths || []);
  const [busy, setBusy] = useState(false);

  const addService = () => setServices([...services, { name: "", price_ngn: 0, description: "" }]);
  const removeService = (i) => setServices(services.filter((_, idx) => idx !== i));
  const updateService = (i, key, val) => {
    const next = [...services];
    next[i] = { ...next[i], [key]: key === "price_ngn" ? Number(val || 0) : val };
    setServices(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/me/profile", { category, location, bio, services });
      await api.put("/me/avatar", { path: avatar || "" });
      await onSaved();
      toast.success("Profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const onAvatarUploaded = async (path) => {
    setAvatar(path);
    await api.put("/me/avatar", { path });
  };
  const clearAvatar = async () => {
    setAvatar("");
    await api.put("/me/avatar", { path: "" });
  };

  const onPortfolioUploaded = async (path) => {
    setPortfolio([...portfolio, path]);
    await api.post("/me/portfolio", { path });
  };
  const removePortfolio = async (path) => {
    setPortfolio(portfolio.filter((p) => p !== path));
    await api.delete(`/me/portfolio?path=${encodeURIComponent(path)}`);
  };

  const isListed = user.listed;

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      {/* LEFT: Photo & visibility */}
      <div className="lg:col-span-4 space-y-6">
        <div className="border border-[#122E1C] bg-[#07140B] p-5">
          <h3 className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] mb-3">// Profile photo</h3>
          <ImageUploader testid="avatar-uploader" square label="Add profile photo" currentPath={avatar} onUploaded={onAvatarUploaded} onClear={clearAvatar} />
        </div>

        <div className="border border-[#122E1C] bg-[#07140B] p-5">
          <h3 className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] mb-3">// Public listing</h3>
          {isListed ? (
            <div className="text-sm">
              <div className="flex items-center gap-2 text-[#00E676] font-mono-jb mb-2">
                <span className="w-2 h-2 bg-[#00E676] rounded-full" /> LIVE on /creatives
              </div>
              <p className="text-xs text-[#8A9F90]">Your profile is visible on the public directory. Brands can discover you and book through Nasarawa Expo.</p>
            </div>
          ) : (
            <div className="text-sm">
              <div className="flex items-center gap-2 text-[#F4B41A] font-mono-jb mb-2">
                <span className="w-2 h-2 bg-[#F4B41A] rounded-full" /> PENDING REVIEW
              </div>
              <p className="text-xs text-[#8A9F90]">Complete your profile (photo, bio, services) and an admin will list you on the public directory.</p>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: bio, services, portfolio */}
      <div className="lg:col-span-8 space-y-6">
        <div className="border border-[#122E1C] bg-[#07140B] p-6 space-y-4">
          <h3 className="font-display font-bold text-2xl tracking-tight">About you</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Category</Label>
              <select
                data-testid="profile-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-2 w-full bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none focus:border-[#00E676] outline-none"
              >
                <option value="">Pick a category…</option>
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">City / Location</Label>
              <Input data-testid="profile-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Lafia, Nasarawa" className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Bio</Label>
            <Textarea data-testid="profile-bio" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell brands what you do best, your style, signature work, awards…" rows={5} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" />
          </div>
        </div>

        <div className="border border-[#122E1C] bg-[#07140B] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-2xl tracking-tight">Services & rates</h3>
            <Button type="button" onClick={addService} variant="ghost" data-testid="add-service-btn" className="text-[#00E676] hover:bg-[#0D2114]">
              <Plus className="w-4 h-4 mr-1" /> Add service
            </Button>
          </div>
          {services.length === 0 && <div className="text-sm text-[#8A9F90] border border-dashed border-[#122E1C] p-6 text-center">No services yet. Add at least one — "Wedding shoot", "Music video", "Brand ambassador day" — so brands know what to book.</div>}
          <div className="space-y-3">
            {services.map((s, i) => (
              <div key={i} data-testid={`service-row-${i}`} className="grid grid-cols-12 gap-2 border border-[#122E1C] p-3">
                <input data-testid={`service-name-${i}`} placeholder="Service name (e.g. Music video shoot)" value={s.name} onChange={(e) => updateService(i, "name", e.target.value)} className="col-span-12 md:col-span-5 bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none" />
                <input data-testid={`service-price-${i}`} type="number" placeholder="Price (₦)" value={s.price_ngn} onChange={(e) => updateService(i, "price_ngn", e.target.value)} className="col-span-6 md:col-span-3 bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none font-mono-jb" />
                <input data-testid={`service-desc-${i}`} placeholder="Short description" value={s.description} onChange={(e) => updateService(i, "description", e.target.value)} className="col-span-5 md:col-span-3 bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none" />
                <button onClick={() => removeService(i)} data-testid={`service-remove-${i}`} className="col-span-1 text-[#FF3B30] flex justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          {services.length > 0 && (
            <div className="text-xs text-[#8A9F90] font-mono-jb mt-3">
              Starting at <span className="text-[#00E676]">{nairaFormat(Math.min(...services.map((s) => s.price_ngn || 0)))}</span>
            </div>
          )}
        </div>

        <div className="border border-[#122E1C] bg-[#07140B] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-2xl tracking-tight">Portfolio</h3>
            <span className="text-xs text-[#8A9F90] font-mono-jb">{portfolio.length} / 12 images</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {portfolio.map((p) => (
              <div key={p} className="relative aspect-square bg-[#030A06] border border-[#122E1C] overflow-hidden" data-testid={`portfolio-item-${p}`}>
                <img src={imgUrl(p)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removePortfolio(p)} data-testid={`portfolio-remove-${p}`} className="absolute top-1 right-1 bg-[#030A06] border border-[#FF3B30] text-[#FF3B30] p-1"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            {portfolio.length < 12 && (
              <ImageUploader testid="portfolio-uploader" square label="Add work" onUploaded={onPortfolioUploaded} />
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <Button data-testid="profile-save-btn" disabled={busy} onClick={save} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none px-8 py-6">
            {busy ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </div>
    </div>
  );
}
